import t from '@jetshop/intl';
import React from 'react';
import styled from 'react-emotion';
import posed from 'react-pose';
import PageHeader from '../PageHeader/PageHeader';
import MaxWidth from '../MaxWidth';
import PageHeaderLoadingState from '../PageHeader/PageHeaderLoadingState';
import Filters from '../../CategoryPage/Filters';
import ProductGridWindow from './ProductGridWindow';
import { useLocation } from 'react-router';
import qs from 'qs';
import useShopId from '../../../utils/useShopId';
import { EztradStyling } from './EztradStyling';
import { Link } from 'react-router-dom';

const Container = styled(MaxWidth)`
  padding-top: 2.1875rem;
  ${({ theme }) => theme.below.md} {
    padding-top: 1.25rem;
  }
`;

const ButtonWrapper = styled('div')`
  width: 100%;
  display: flex;
  justify-content: center;

  a {
    font-weight: 500;
    font-size: 0.9375rem;
    letter-spacing: 0.05em;
    font-variant: small-caps;
    text-transform: lowercase;
    text-decoration: none;

    color: ${({ theme }) => theme.colors.green};
    padding-bottom: 3px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.green};
    cursor: pointer;
  }
`;

// Expand the filters out from 0 height to full height once they load
const FilterAnimation = posed.div({
  closed: {
    height: 0,
    opacity: 0,
    overflow: 'hidden'
  },
  open: {
    height: 'auto',
    opacity: 1,
    duration: 600,
    applyAtEnd: { overflow: 'visible' }
  }
});

// Fade in the product grid once it loads
const ContainerAnimation = posed.div({
  closed: {
    opacity: 0
  },
  open: { opacity: 1 }
});

// This wraps the filters and the product grid and staggers their animation.
// So the FilterAnimation will run first, and then 200ms later the
// ContainerAnimation will run.
const StaggerAnimation = posed.div({
  open: { staggerChildren: 200 }
});

const BottomSection = styled('div')`
  margin-bottom: 3.25rem;

  .product-amount-info {
    font-weight: 500;
    font-size: 0.75rem;
    color: ${({ theme }) => theme.colors.grey};
    text-align: center;
    margin-bottom: 1.5rem;
  }
`;

const WindowedPage = ({
  infinitePaginationProps,
  cacheHit,
  loading,
  categoryPath,
  filters,
  sortOrders,
  title,
  total,
  images,
  headerContent,
  breadCrumbText,
  parents
}) => {
  const isEztrad = useShopId() === 'eztrad';
  const { previous, next, products } = infinitePaginationProps;

  const location = useLocation();
  const params = qs.parse(location.search, { ignoreQueryPrefix: true });

  return (
    <div className={isEztrad ? EztradStyling : ''}>
      {!cacheHit ? (
        // Show a dummy header if we don't have cached data
        <PageHeaderLoadingState />
      ) : (
        <PageHeader
          parents={parents}
          breadcrumbText={breadCrumbText}
          content={headerContent}
          title={title}
          images={images}
        />
      )}

      <Container>
        <StaggerAnimation pose={cacheHit && 'open'}>
          <FilterAnimation pose={cacheHit ? 'open' : 'closed'}>
            <Filters filters={filters} sortOrders={sortOrders} />
          </FilterAnimation>
          <ContainerAnimation pose={cacheHit ? 'open' : 'closed'}>
            {previous.hasProducts && (
              <ButtonWrapper className="show-more-button">
                <Link
                  onClick={e => {
                    e.preventDefault();
                    previous.fetchProducts();
                  }}
                  to={{
                    ...location,
                    search: qs.stringify({
                      ...params,
                      page: previous.page
                    })
                  }}
                >
                  {t('Show more')}
                </Link>
              </ButtonWrapper>
            )}
            <div className="product-grid-window-container">
              <ProductGridWindow
                prevOffset={previous.offset}
                products={products}
                listName={title}
                categoryPath={categoryPath}
                loading={loading}
                imageSizes={[1 / 2, 1 / 2, 1 / 3, 1 / 4]}
              />
            </div>
            <BottomSection>
              <div className="product-amount-info">
                {t.rich('Showing {productsAmount} of {total}', {
                  productsAmount: products.length,
                  total
                })}
              </div>
              {next.hasProducts && (
                <ButtonWrapper className="show-more-button">
                  <Link
                    onClick={e => {
                      e.preventDefault();
                      next.fetchProducts();
                    }}
                    to={{
                      ...location,
                      search: qs.stringify({
                        ...params,
                        page: next.page
                      })
                    }}
                  >
                    {t('Show more')}
                  </Link>
                </ButtonWrapper>
              )}
            </BottomSection>
          </ContainerAnimation>
        </StaggerAnimation>
      </Container>
    </div>
  );
};

export default WindowedPage;
